import { computed } from 'vue';
import { defineStore } from 'pinia';
import { formatAngle } from '@/util/format/formatAngle';
import { formatLengthSign } from '@/util/format/formatLengthSign';
import { formatPercentageNumber } from '@/util/format/formatPercentageNumber';
import { vector3 } from '@/domain/geometry/vector3';
import { positionalPart } from '@/domain/geometry/matrix';
import type { Adjustments } from '@/views/3d-planner/store/plannerState';
import { usePlannerStore } from '@/views/3d-planner/store/plannerStore';
import { usePlannerSceneViewModel } from '@/views/3d-planner/store/plannerSceneViewModel';
import { useCupOverlayViewModel } from '@/views/3d-planner/cup/useCupOverlayViewModel';
import type { PlannerStore } from '@/views/3d-planner/store/plannerStore.types';
import type { PlannerSceneViewModel } from '@/views/3d-planner/store/plannerSceneViewModel.types';
import type { CupOverlayViewModel } from '@/views/3d-planner/cup/cupOverlay.types';
import {
    calculateLegLengthAndOffset,
    calculateStemAnteversion,
} from '@/views/3d-planner/measurements/adjustments';
import {
    type AdjustmentInfo,
    type AdjustmentKey,
    createAdjustment,
} from '@/views/3d-planner/measurements/adjustmentsUi';
import {
    createHipLengthDifferenceMeasurement,
    createMeasurement,
    type MeasurementInfo,
    type MeasurementKey,
} from '@/views/3d-planner/measurements/measurementsUi';

/**
 * The geometric stem anteversion, or null when the stem/femur geometry isn't available yet.
 * Reads the neck axis and stem-group transform off the Model (`fittedStem`/`template.stemTransform`),
 * the way `resolveLtResection` does, rather than off the scene's `stemNeckAxis` node — that node is
 * left at its default (0, 1, 0) direction for a legacy stem (see `updateStem.ts` → `_updateStemAxes`),
 * which would otherwise read as a meaningless but plausible-looking angle. `neckAxisDirection` is
 * always present on a fitted stem (see {@link FittedStem.neckAxisDirection}), so this is available
 * even for a legacy stem with no manual-positioning axes.
 */
function stemAnteversionValue(planner: PlannerStore): number | null {
    const { femoralFeatures, fittedStem, template } = planner;
    if (!femoralFeatures || planner.isLoading || !template || !fittedStem) {
        return null;
    }
    return calculateStemAnteversion({
        posterior: femoralFeatures.shaftBasis.posterior,
        condylarAxis: vector3(femoralFeatures.anteversionCondylar),
        stemGroupTransform: fittedStem.transform.clone().multiply(template.stemTransform),
        stemNeckDirection: fittedStem.neckAxisDirection,
    });
}

/**
 * Live leg-length and offset vs. native, or null while inputs are incomplete. Deep-reads the
 * scene's `cupGroup.transform` (mutated in place by the cup-drag writers), so it lives here beside
 * the stem-anteversion computation rather than on the Model — both project scene node transforms
 * into measurements.
 */
function adjustmentsValue(planner: PlannerStore, scene: PlannerSceneViewModel): Adjustments | null {
    const { fittedCup, fittedStem, template } = planner;
    const caseInfo = planner.case;
    if (!fittedCup || !fittedStem || !template || !caseInfo || planner.isLoading) {
        return null;
    }
    return calculateLegLengthAndOffset(
        caseInfo.operationalSide,
        fittedCup.basis,
        positionalPart(scene.nodes.cupGroup.transform),
        positionalPart(template.stemTransform).applyMatrix4(fittedStem.transform),
    );
}

/** Package the stem anteversion for the panel: warns when it deviates from native by > 10°. */
function buildStemAnteversionAdjustment(
    value: number | null,
    nativeFemoralAnteversion: number | null,
    calculating: boolean,
): AdjustmentInfo {
    return createAdjustment(
        'Stem atv.',
        formatAngle,
        value === null || nativeFemoralAnteversion === null
            ? null
            : { calculating, warning: Math.abs(value - nativeFemoralAnteversion) > 10, value },
    );
}

function buildLegOrOffsetAdjustment(
    label: string,
    value: number | undefined,
    target: number | undefined,
    calculating: boolean,
): AdjustmentInfo {
    return createAdjustment(
        label,
        formatLengthSign,
        value === undefined || target === undefined
            ? null
            : { calculating, warning: Math.abs(value - target) > 10, value },
    );
}

function buildCupCoverageAdjustment(cup: CupOverlayViewModel): AdjustmentInfo {
    const formatCoverage = (): string => {
        const coverage = cup.coverage;
        return coverage === null || coverage === 'calculating'
            ? '--'
            : formatPercentageNumber(coverage);
    };
    const coverage = cup.coverage;
    if (coverage === 'calculating') {
        return createAdjustment('Cup coverage', formatCoverage, {
            calculating: true,
            warning: false,
            value: null,
        });
    }
    return createAdjustment('Cup coverage', formatCoverage, {
        calculating: false,
        warning: coverage === null ? false : coverage < 0.5,
        value: coverage ?? null,
    });
}

function buildAdjustments(
    planner: PlannerStore,
    legAdjustments: Adjustments | null,
    cup: CupOverlayViewModel,
    stemAnteversion: number | null,
): Record<AdjustmentKey, AdjustmentInfo> {
    const calculating = planner.currentOperation === 'set-targets';
    return {
        'stem-atv': buildStemAnteversionAdjustment(
            stemAnteversion,
            planner.nativeMeasurements?.femoralAnteversion ?? null,
            calculating,
        ),
        'leg-length': buildLegOrOffsetAdjustment(
            'Leg length',
            legAdjustments?.legLength,
            planner.targetAdjustments?.legLength,
            calculating,
        ),
        'leg-offset': buildLegOrOffsetAdjustment(
            'Leg offset',
            legAdjustments?.offset,
            planner.targetAdjustments?.offset,
            calculating,
        ),
        'cup-atv': createAdjustment('Cup Atv.', formatAngle, cup.anteversion),
        'cup-inc': createAdjustment('Cup Inc.', formatAngle, cup.inclination),
        'cup-coverage': buildCupCoverageAdjustment(cup),
    };
}

function buildNativeMeasurements(planner: PlannerStore): Record<MeasurementKey, MeasurementInfo> {
    return {
        'hip-length-difference': createHipLengthDifferenceMeasurement(
            planner.nativeMeasurements?.hipLengthDifference,
            planner.diceScores,
        ),
        'femur-anteversion': createMeasurement(
            'Femur Anteversion',
            formatAngle,
            planner.nativeMeasurements.femoralAnteversion,
        ),
        'acetabular-anteversion': createMeasurement(
            'Acetabular Anteversion',
            formatAngle,
            planner.nativeMeasurements.acetabularAnteversion,
        ),
        'acetabular-inclination': createMeasurement(
            'Acetabular Inclination',
            formatAngle,
            planner.nativeMeasurements.acetabularInclination,
        ),
    };
}

/**
 * ViewModel for the adjustments + native-measurements panel. Owns every derived display value,
 * so AdjustmentsMeasurements.vue is presentation-only and cross-feature consumers (hip-spine
 * analysis) read the single stem-anteversion computation from here rather than recomputing it.
 */
export const useAdjustmentsMeasurementsViewModel = defineStore('adjustments-measurements', () => {
    const plannerStore = usePlannerStore();
    const scene = usePlannerSceneViewModel();
    const cupStore = useCupOverlayViewModel();

    const stemAnteversion = computed(() => stemAnteversionValue(plannerStore));
    const legAdjustments = computed(() => adjustmentsValue(plannerStore, scene));
    const adjustments = computed(() =>
        buildAdjustments(plannerStore, legAdjustments.value, cupStore, stemAnteversion.value),
    );
    const nativeMeasurements = computed(() => buildNativeMeasurements(plannerStore));

    return { stemAnteversion, adjustments, nativeMeasurements };
});